import React from "react";
import ButtonNew from "components/buttonNew/buttonNew";
import { Plus, Trash } from "@phosphor-icons/react";
import { CompanyEmployee } from "shared/types/yally-company";
import { Contact } from "shared/types/contact";
import { ContactSearchSelect } from "screens/buildings/components/renovationTab/modals/components/ContactSearchSelect";

interface CompanyEmployeeListProps {
  employees: CompanyEmployee[];
  onChange: (employees: CompanyEmployee[]) => void;
  isDisabled?: boolean;
}

const toContactValue = (employee: CompanyEmployee): Contact | null => {
  return {
    id: employee.id,
    firstName: employee.firstName,
    lastName: employee.lastName,
    createdAt: new Date(),
  };
};

const CompanyEmployeeList: React.FC<CompanyEmployeeListProps> = ({
  employees,
  onChange,
  isDisabled = false,
}) => {
  const handleAdd = () => {
    const placeholder: CompanyEmployee = { id: 0, firstName: "", lastName: "" };
    onChange([...employees, placeholder]);
  };

  const handleRemove = (index: number) => {
    onChange(employees.filter((_, i) => i !== index));
  };

  const handleContactChange = (index: number, contact: Contact | null) => {
    const updated = employees.map((employee, i) => {
      if (i !== index) return employee;
      if (!contact) return { id: 0, firstName: "", lastName: "" };
      return { id: contact.id, firstName: contact.firstName, lastName: contact.lastName };
    });
    onChange(updated);
  };

  const isPlaceholderEntry = (employee: CompanyEmployee): boolean => {
    return employee.id === 0 && employee.firstName === "" && employee.lastName === "";
  };

  return (
    <div className="flex flex-col gap-3">
      {employees.length === 0 && (
        <p className="text-sm text-gray-400">Geen medewerkers toegevoegd</p>
      )}

      {employees.map((employee, index) => (
        <div
          key={isPlaceholderEntry(employee) ? `new-${index}` : employee.id}
          className="flex items-center gap-3 rounded-lg border border-gray-200 p-3"
        >
          <div className="flex-1">
            <ContactSearchSelect
              value={isPlaceholderEntry(employee) ? null : toContactValue(employee)}
              onChange={(contact) => handleContactChange(index, contact)}
              placeholder="Zoek medewerker..."
            />
          </div>

          {!isDisabled && (
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
            >
              <Trash size={16} />
            </button>
          )}
        </div>
      ))}

      {!isDisabled && (
        <ButtonNew
          variant="secondaryMoss"
          onClick={handleAdd}
          icon={<Plus />}
          label="Medewerker toevoegen"
        />
      )}
    </div>
  );
};

export default CompanyEmployeeList;
